'use client'

import { type RefObject, useEffect } from 'react'

/**
 * Lets iOS Safari extend a text selection inside an open drawer.
 *
 * Dragging a selection handle on iOS fires touchmove events. A modal vaul
 * drawer has two document-level scroll locks (vaul's own on iOS, and Radix
 * Dialog's react-remove-scroll) that cancel any touchmove over content that
 * can't scroll, so the selection gets stuck on the first word.
 *
 * A window capture listener runs before both. While a selection is active
 * and the touch is inside the drawer, it stops the event from reaching
 * them. Swipe-to-close uses pointer events, so it isn't affected, and vaul
 * already refuses to drag while text is selected.
 */
export function useAllowSelectionDrag(
  isOpen: boolean,
  contentRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!isOpen) {
      return
    }

    function touchMoveHandler(event: TouchEvent) {
      const selection = window.getSelection()

      if (!selection || selection.isCollapsed) {
        return
      }

      if (!contentRef.current?.contains(event.target as Node)) {
        return
      }

      event.stopPropagation()
    }

    window.addEventListener('touchmove', touchMoveHandler, {
      capture: true,
      passive: true,
    })

    return () => {
      window.removeEventListener('touchmove', touchMoveHandler, {
        capture: true,
      })
    }
  }, [isOpen, contentRef])
}
